<template>
  <div class="form-page">
    <div class="form-page__header">
      <h2 class="form-page__title">Angaben zur antragstellenden Person</h2>
      <p class="form-page__subtitle">Bitte füllen Sie alle Pflichtfelder aus.</p>
    </div>

    <div class="form-section">
      <h3 class="section-title">Persönliche Daten</h3>
      <div class="field-group">

        <!-- Anrede + Titel in one row -->
        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">
              Anrede <span class="required-mark">*</span>
            </label>
            <select
              class="field__select"
              :class="{ 'field--error': showErrors && !modelValue.anrede }"
              :value="modelValue.anrede"
              @change="update('anrede', $event.target.value)"
            >
              <option value="">– Bitte wählen –</option>
              <option value="Frau">Frau</option>
              <option value="Herr">Herr</option>
              <option value="Divers">Divers / Keine Angabe</option>
            </select>
            <p v-if="showErrors && !modelValue.anrede" class="field__error">Bitte wählen Sie eine Anrede aus.</p>
          </div>

          <div class="field">
            <label class="field__label">Titel (optional)</label>
            <input
              type="text"
              class="field__input"
              placeholder="z. B. Dr., Prof."
              :value="modelValue.titel"
              @input="update('titel', $event.target.value)"
            />
          </div>
        </div>

        <!-- Künstlername -->
        <div class="field">
          <label class="field__label">Künstlername (optional)</label>
          <input
            type="text"
            class="field__input"
            placeholder="Falls abweichend vom bürgerlichen Namen"
            :value="modelValue.kuenstlername"
            @input="update('kuenstlername', $event.target.value)"
          />
        </div>

        <!-- Vorname + Nachname -->
        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">
              Vorname <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.vorname }"
              placeholder="Vorname"
              :value="modelValue.vorname"
              @input="update('vorname', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.vorname" class="field__error">Bitte geben Sie Ihren Vornamen ein.</p>
          </div>

          <div class="field">
            <label class="field__label">
              Nachname <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.nachname }"
              placeholder="Nachname"
              :value="modelValue.nachname"
              @input="update('nachname', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.nachname" class="field__error">Bitte geben Sie Ihren Nachnamen ein.</p>
          </div>
        </div>

        <!-- Geschlecht -->
        <div class="field">
          <label class="field__label">
            Geschlecht <span class="required-mark">*</span>
          </label>
          <select
            class="field__select"
            :class="{ 'field--error': showErrors && !modelValue.geschlecht }"
            :value="modelValue.geschlecht"
            @change="update('geschlecht', $event.target.value)"
          >
            <option value="">– Bitte wählen –</option>
            <option value="weiblich">Weiblich</option>
            <option value="männlich">Männlich</option>
            <option value="divers">Divers</option>
            <option value="keine Angabe">Keine Angabe</option>
          </select>
          <p v-if="showErrors && !modelValue.geschlecht" class="field__error">Bitte wählen Sie ein Geschlecht aus.</p>
        </div>

        <!-- Geburtsdatum + Geburtsort -->
        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">
              Geburtsdatum <span class="required-mark">*</span>
            </label>
            <input
              type="date"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.geburtsdatum }"
              :value="modelValue.geburtsdatum"
              @input="update('geburtsdatum', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.geburtsdatum" class="field__error">Bitte geben Sie Ihr Geburtsdatum an.</p>
          </div>

          <div class="field">
            <label class="field__label">
              Geburtsort <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.geburtsort }"
              placeholder="Geburtsort"
              :value="modelValue.geburtsort"
              @input="update('geburtsort', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.geburtsort" class="field__error">Bitte geben Sie Ihren Geburtsort an.</p>
          </div>
        </div>

        <!-- Staatsangehörigkeit -->
        <div class="field">
          <label class="field__label">
            Staatsangehörigkeit <span class="required-mark">*</span>
          </label>
          <select
            class="field__select"
            :class="{ 'field--error': showErrors && !modelValue.staatsangehoerigkeit }"
            :value="modelValue.staatsangehoerigkeit"
            @change="update('staatsangehoerigkeit', $event.target.value)"
          >
            <option value="">– Bitte wählen –</option>
            <option value="deutsch">Deutsch</option>
            <option value="österreichisch">Österreichisch</option>
            <option value="schweizerisch">Schweizerisch</option>
            <option value="amerikanisch">Amerikanisch (USA)</option>
            <option value="britisch">Britisch</option>
            <option value="französisch">Französisch</option>
            <option value="italienisch">Italienisch</option>
            <option value="polnisch">Polnisch</option>
            <option value="russisch">Russisch</option>
            <option value="türkisch">Türkisch</option>
            <option value="sonstige">Sonstige</option>
          </select>
          <p v-if="showErrors && !modelValue.staatsangehoerigkeit" class="field__error">Bitte wählen Sie Ihre Staatsangehörigkeit aus.</p>
        </div>
      </div>
    </div>

    <div class="form-section">
      <h3 class="section-title">Kontaktdaten</h3>
      <div class="field-group">
        <div class="field">
          <label class="field__label">
            E-Mail-Adresse <span class="required-mark">*</span>
          </label>
          <input
            type="email"
            class="field__input"
            :class="{ 'field--error': showErrors && !modelValue.email }"
            placeholder="beispiel@email.de"
            :value="modelValue.email"
            @input="update('email', $event.target.value)"
          />
          <p v-if="showErrors && !modelValue.email" class="field__error">Bitte geben Sie Ihre E-Mail-Adresse ein.</p>
        </div>

        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">
              Telefonnummer <span class="required-mark">*</span>
            </label>
            <input
              type="tel"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.telefon }"
              placeholder="+49 30 12345678"
              :value="modelValue.telefon"
              @input="update('telefon', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.telefon" class="field__error">Bitte geben Sie Ihre Telefonnummer an.</p>
          </div>

          <div class="field">
            <label class="field__label">Mobiltelefon (optional)</label>
            <input
              type="tel"
              class="field__input"
              placeholder="+49 170 1234567"
              :value="modelValue.mobil"
              @input="update('mobil', $event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="form-section">
      <h3 class="section-title">Adresse</h3>
      <div class="field-group">
        <div class="field-row field-row--2" style="grid-template-columns: 2fr 1fr;">
          <div class="field">
            <label class="field__label">
              Straße <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.strasse }"
              placeholder="Musterstraße"
              :value="modelValue.strasse"
              @input="update('strasse', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.strasse" class="field__error">Pflichtfeld</p>
          </div>

          <div class="field">
            <label class="field__label">
              Hausnummer <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.hausnummer }"
              placeholder="42a"
              :value="modelValue.hausnummer"
              @input="update('hausnummer', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.hausnummer" class="field__error">Pflichtfeld</p>
          </div>
        </div>

        <div class="field-row field-row--2" style="grid-template-columns: 1fr 2fr;">
          <div class="field">
            <label class="field__label">
              Postleitzahl <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.plz }"
              placeholder="10115"
              maxlength="5"
              :value="modelValue.plz"
              @input="update('plz', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.plz" class="field__error">Pflichtfeld</p>
          </div>

          <div class="field">
            <label class="field__label">
              Ort <span class="required-mark">*</span>
            </label>
            <input
              type="text"
              class="field__input"
              :class="{ 'field--error': showErrors && !modelValue.ort }"
              placeholder="Berlin"
              :value="modelValue.ort"
              @input="update('ort', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.ort" class="field__error">Pflichtfeld</p>
          </div>
        </div>
      </div>
    </div>

    <p class="required-note"><span class="required-mark">*</span> Pflichtfelder</p>
  </div>
</template>

<script>
export default {
  name: 'LitStep4',
  props: {
    modelValue: { type: Object, required: true },
    showErrors: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const update = (key, val) => emit('update:modelValue', { ...props.modelValue, [key]: val });
    return { update };
  },
};
</script>

<style scoped src="../form.css"></style>
